import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { setTokens, logout, setMe } from "./authSlice";

const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${baseUrl}/api`,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.access;
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return headers;
  },
});

let refreshingPromise: Promise<any> | null = null;

const baseQueryWithReauth: typeof rawBaseQuery = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && (result.error as any).status === 401) {
    const state = api.getState() as RootState;
    const refresh = state.auth.refresh;

    if (!refresh) {
      api.dispatch(logout());
      return result;
    }

    // ✅ single-flight refresh with try/finally (NO .finally on rawBaseQuery)
    if (!refreshingPromise) {
      refreshingPromise = (async () => {
        try {
          return await rawBaseQuery(
            {
              url: "/auth/token/refresh",
              method: "POST",
              body: { refresh },
            },
            api,
            extraOptions
          );
        } finally {
          refreshingPromise = null;
        }
      })();
    }

    const refreshResult = await refreshingPromise;

    // refreshResult is QueryReturnValue (either {data} or {error})
    const access = (refreshResult as any)?.data?.access;
    const newRefresh = (refreshResult as any)?.data?.refresh;

    if (access) {
      api.dispatch(
        setTokens({
          access,
          refresh: newRefresh || refresh, // rotation might return refresh
        })
      );

      // retry original request with new access token
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logout());
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Me", "Transfers", "Series", "Clients", "Providers", "Vehicles", "Locations", "Analytics"],
  endpoints: (builder) => ({
    login: builder.mutation<
      any,
      { email: string; password: string }
    >({
      query: (body) => ({
        url: "/auth/login",
        method: "POST",
        body,
      }),
    }),

    register: builder.mutation<
      any,
      {
        agency_name: string;
        owner_email: string;
        owner_password: string;
        owner_first_name?: string;
        owner_last_name?: string;
      }
    >({
      query: (body) => ({
        url: "/public/register-agency",
        method: "POST",
        body,
      }),
    }),

    me: builder.query<any, void>({
      query: () => ({ url: "/auth/me", method: "GET" }),
      providesTags: ["Me"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            setMe({
              user: data.user,
              activeAgency: data.active_agency ?? null,
              permissions: data.permissions ?? [],
            })
          );
        } catch {
          // route guard handles
        }
      },
    }),
  }),
});

export const { useLoginMutation, useRegisterMutation, useMeQuery, useLazyMeQuery } = api;
